import {
  AssetStore,
  ComponentType,
  dispatch,
  GuidHelper,
  IApplicationComponentModel,
  IApplicationComponentPropertyValueModel,
  IApplicationComponentTypeModel,
  IErrorModel,
  PlatformType,
  RecordStatus,
} from "@xala/common-services";
import {
  Button,
  Empty,
  Heading,
  Icon,
  ITableColumnProps,
  ITableRowSelection,
  Modal,
  NotificationService,
  SectionGrid,
  SectionGridItem,
  Sider,
  TableWithDraggableSorter,
} from "@xala/common-ui";
import React, {
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { ApplicationScreenContext } from "../../context";
import "./ApplicationBannerItemsModal.scss";
import { ComponentTypeHelper, PlatformTypeHelper } from "../../../../helpers";
import { useSelector } from "react-redux";
import { IAppState } from "@xala/studio/src/store";
import { DesignerModule } from "../../modules";
import {
  getItemsPropertyValuesFromComponent,
  getUndeletedComponents,
  getValuesFromItemsProperty,
  sortComponentsBySequence,
} from "./utils";

const notificationService = NotificationService.getInstance();

export interface IApplicationBannerItemAddModalProps {
  isLoadingData?: boolean;
  modalVisible: boolean;
  onItemsModalCancel: SetStateAction<any>;
  selectedRowKeys?: React.Key[];
  addComponentModalVisible?: boolean;
}

export const ApplicationBannerItemsModal = ({
  ...props
}: IApplicationBannerItemAddModalProps) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [hasChanges, setHasChanges] = useState(false);

  const [bannerItems, setBannerItems] = useState<
    IApplicationComponentPropertyValueModel[]
  >([]);

  const [selectedBannerItem, setSelectedBannerItem] = useState<
    IApplicationComponentPropertyValueModel | undefined
  >();

  const { screen, component, onNewChanges } = useContext(
    ApplicationScreenContext
  );

  const { onItemsModalCancel, modalVisible, isLoadingData } = props;

  const { t } = useTranslation();

  const getComponentsTypes = useSelector(
    (state: IAppState) => state.applicationConfiguration.componentsTypes
  );

  useEffect(() => {
    const components = getComponents();

    if (!selectedBannerItem) {
      setSelectedBannerItem(components[0]);
    }

    setBannerItems(components);
    setHasChanges(false);
  }, [hasChanges]);

  const getComponents = (): IApplicationComponentPropertyValueModel[] => {
    if (bannerItems.length === 0 && component) {
      let components: IApplicationComponentPropertyValueModel[] = [];

      let getItemsProperty = getItemsPropertyValuesFromComponent(component);

      if (getItemsProperty) {
        components = getValuesFromItemsProperty(getItemsProperty);
      }

      let sortedComponents = sortComponentsBySequence(components) || components;
      setBannerItems(sortedComponents || []);

      return sortedComponents;
    } else {
      let sortedComponents = sortComponentsBySequence(bannerItems);
      return sortedComponents;
    }
  };

  const onComponentAdd = (itemComponent: IApplicationComponentModel) => {
    if (!component || !component.Properties || !screen) {
      return;
    }

    if (!screen.Components) {
      screen.Components = [];
    }

    const newComponent: IApplicationComponentModel = {
      ...itemComponent,
    };

    const getPropertyValues = [
      ...(component.Properties.find((component) => component.Name === "Items")
        ?.Values || []),
    ];

    const minId =
      getPropertyValues.length > 0
        ? getPropertyValues.reduce(
            (min, component) => (component.Id < min ? component.Id : min),
            getPropertyValues[0].Id
          )
        : 1;

    newComponent.Id = minId >= 0 ? -1 : minId - 1;
    newComponent.ApplicationConfigurationId = screen.ApplicationConfigurationId;
    newComponent.IsVisible = true;
    newComponent.RecordStatus = RecordStatus.Inserted;
    newComponent.Sequence =
      getPropertyValues.length > 0
        ? Math.max(
            ...getPropertyValues.map((c) => {
              return c.Component ? c.Component.Sequence + 1 : 0;
            })
          )
        : 0;

    if (!newComponent.Guid) {
      newComponent.Guid = GuidHelper.newGuid();
    }

    if (!newComponent.PlatformCode) {
      newComponent.PlatformCode = PlatformType.Any;
    }

    const findHighestSequencyValue =
      bannerItems.length > 0
        ? [...bannerItems]
            .map((item) => item.ApplicationComponentPropertyValueSequence)
            .reduce((acc, val) => {
              if (acc && val) {
                return Math.max(val, acc);
              } else return 0;
            })
        : 0;

    setBannerItems((prevState) => [
      ...prevState,
      {
        Id: minId,
        Guid: GuidHelper.newGuid(),
        ApplicationComponentPropertyId: component.Id,
        ApplicationComponentPropertyValueSequence:
          findHighestSequencyValue! + 1 || 0,
        RecordStatus: RecordStatus.Inserted,
        Component: newComponent,
      },
    ]);

    if (onNewChanges) {
      onNewChanges(true);
      setHasChanges(true);
    }
  };

  const onTableRow = (component: IApplicationComponentModel) => {
    const updateSelectedComponent = bannerItems.filter(
      (items) => items.Component?.Guid === component.Guid
    )[0];

    return {
      onClick: () => {
        setSelectedBannerItem(updateSelectedComponent);
        setSelectedRowKeys([component.Guid]);
      },
    };
  };

  const onDeleteComponent = (
    itemToDelete: IApplicationComponentPropertyValueModel
  ) => {
    if (!bannerItems) {
      return;
    }

    if (itemToDelete) {
      if (itemToDelete.RecordStatus === RecordStatus.Inserted) {
        const filterBanner = bannerItems.filter(
          (items) => items.Component?.Guid !== itemToDelete.Guid
        );

        setBannerItems(filterBanner);
      } else {
        const filterBanner = bannerItems.map((items) => {
          if (items.Component) {
            if (items.Component.Id === itemToDelete.Id) {
              items.RecordStatus = RecordStatus.Deleted;
              items.Component.RecordStatus = RecordStatus.Deleted;
            }
            return items;
          }
        });

        setBannerItems(
          filterBanner as IApplicationComponentPropertyValueModel[]
        );
      }
    }

    if (onNewChanges) {
      onNewChanges(true);
      setSelectedBannerItem(undefined);
      setHasChanges(true);
    }
  };

  const onDeleteClick = (item: any) => {
    Modal.confirm({
      title: t("Delete item"),
      content: t(`Are you sure you want to delete item?`),
      okText: "OK",
      cancelText: "Cancel",
      onOk: () => onDeleteComponent(item),
    });
  };

  const getColumnsProps = (): Array<
    ITableColumnProps<IApplicationComponentModel>
  > => {
    const { t } = useTranslation();

    return [
      {
        width: "150px",
        key: "ComponentTypeCode",
        dataIndex: "ComponentTypeCode",
        title: t("type", "Type"),
        render: (text: any, row: IApplicationComponentModel) =>
          ComponentTypeHelper.getTag(row.ComponentTypeCode),
      },
      {
        width: "190px",
        key: "ComponentTitle",
        dataIndex: "ComponentTitle",
        title: t("MODEL_TITLE"),
        render: (_, row: IApplicationComponentModel) => {
          const title = row.Properties?.find(
            (property) => property?.Name === "Title"
          )?.Value?.StringValue;
          return <p className="ComponentTitle">{title}</p>;
        },
      },
      {
        width: "120px",
        title: t("platform", "Platform"),
        render: (text: any, row: IApplicationComponentModel) =>
          PlatformTypeHelper.getTag(row.PlatformCode),
      },
      {
        key: "RemoveItemAction",
        dataIndex: "RemoveItemAction",
        title: t("Action", "Action"),
        width: "80px",
        render: (text: any, row: IApplicationComponentModel) => (
          <Button
            danger={true}
            icon={<Icon type="delete" />}
            title={t("DELETE_ITEM", "Delete item")}
            onClick={() => onDeleteClick(row)}
          />
        ),
      },
    ];
  };

  const onMoveRow = (dragIndex: number, hoverIndex: number) => {
    const components = [...bannerItems];

    if (!components) {
      return;
    }

    const draggedComponent = components[dragIndex];
    const hoveredComponent = components[hoverIndex];

    if (!draggedComponent || !hoveredComponent) {
      return;
    }

    let componentsToUpdate: IApplicationComponentPropertyValueModel[] = [
      {
        ...draggedComponent,
        ApplicationComponentPropertyValueSequence:
          draggedComponent.ApplicationComponentPropertyValueSequence ===
          hoveredComponent.ApplicationComponentPropertyValueSequence
            ? hoveredComponent.ApplicationComponentPropertyValueSequence! + 1
            : hoveredComponent.ApplicationComponentPropertyValueSequence,
        RecordStatus:
          draggedComponent.RecordStatus === RecordStatus.Inserted
            ? RecordStatus.Inserted
            : RecordStatus.Updated,
      },
    ];

    // Update components order - required to render list before save
    if (
      draggedComponent.ApplicationComponentPropertyValueSequence! >
      hoveredComponent.ApplicationComponentPropertyValueSequence!
    ) {
      componentsToUpdate = componentsToUpdate.concat(
        components
          .filter(
            (c) =>
              c.ApplicationComponentPropertyValueSequence! >=
                hoveredComponent.ApplicationComponentPropertyValueSequence! &&
              c.ApplicationComponentPropertyValueSequence! <
                draggedComponent.ApplicationComponentPropertyValueSequence!
          )
          .map((i) => {
            i.ApplicationComponentPropertyValueSequence!++;
            i.RecordStatus =
              i.RecordStatus === RecordStatus.Inserted
                ? RecordStatus.Inserted
                : RecordStatus.Updated;
            return i;
          })
      );
    } else {
      componentsToUpdate = componentsToUpdate.concat(
        components
          .filter(
            (c) =>
              c.ApplicationComponentPropertyValueSequence! <=
                hoveredComponent.ApplicationComponentPropertyValueSequence! &&
              c.ApplicationComponentPropertyValueSequence! >
                draggedComponent.ApplicationComponentPropertyValueSequence!
          )
          .map((i) => {
            i.ApplicationComponentPropertyValueSequence!--;
            i.RecordStatus =
              i.RecordStatus === RecordStatus.Inserted
                ? RecordStatus.Inserted
                : RecordStatus.Updated;
            return i;
          })
      );
    }

    componentsToUpdate.reduce(
      (
        previous: IApplicationComponentPropertyValueModel,
        current: IApplicationComponentPropertyValueModel,
        index: number
      ) => {
        if (
          componentsToUpdate[index]
            .ApplicationComponentPropertyValueSequence ===
          previous.ApplicationComponentPropertyValueSequence
        ) {
          componentsToUpdate[index].ApplicationComponentPropertyValueSequence =
            componentsToUpdate[index]
              .ApplicationComponentPropertyValueSequence! + 1;
        }
        return current;
      }
    );

    onComponentsChange(componentsToUpdate);
  };

  if (!screen) {
    return <Empty>{t("No screen data")}</Empty>;
  }

  const columns = getColumnsProps();

  const componentsToView = getUndeletedComponents(
    bannerItems
  ) as IApplicationComponentModel[];

  const rowSelection: ITableRowSelection<IApplicationComponentModel> = {
    onChange: (
      selectedRowKeys: React.Key[],
      selectedRows: IApplicationComponentModel[]
    ) => {
      if (selectedRows.length > 0) {
        const selectedComponent = selectedRows[0];

        const updateSelectedComponent = bannerItems.filter(
          (items) => items.Component?.Guid === selectedComponent.Guid
        )[0];

        setSelectedBannerItem(updateSelectedComponent);
      }
    },
    type: "radio",
    selectedRowKeys: [
      bannerItems.findIndex((c) => c.Guid == selectedBannerItem?.Guid),
    ],
  };

  const addBannerItem = () => {
    const componentType = getComponentsTypes.find(
      (row: IApplicationComponentTypeModel) =>
        row.Code === ComponentType.BannerItem
    );

    const componentItemsLength = bannerItems.filter(
      (property) => property.RecordStatus !== RecordStatus.Deleted
    ).length;

    if (componentItemsLength > 1) {
      return notificationService.warning({
        message: t("MAX_ITEMS_ADDED_VALIDATION_MESSAGE", { limit: 2 }),
      });
    }

    if (!componentType) {
      return;
    }

    return new Promise<IApplicationComponentModel>((resolve, reject) => {
      const component: IApplicationComponentModel = {
        Id: -1,
        Guid: GuidHelper.newGuid(),
        Name: componentType.Name,
        ComponentTypeCode: ComponentTypeHelper.getValue(componentType.Code),
        PlatformCode: PlatformType.Any,
        Sequence: 0,
        RecordStatus: RecordStatus.Inserted,
      };
      resolve(component);
    })
      .then((component: IApplicationComponentModel) => {
        onComponentAdd(component);
      })
      .catch((error: IErrorModel) => {});
  };

  const saveChangesOnClose = () => {
    if (onComponentChange && component?.Properties) {
      let newComponent = component?.Properties.find(
        (component) => component.Name === "Items"
      );

      if (newComponent && newComponent.Values) {
        newComponent.Values = bannerItems;

        return onComponentChange(component);
      }
    }
  };

  const zeroWidthTriggerStyle: React.CSSProperties = {
    top: "60px",
    height: "33px",
    fontSize: "18px",
    lineHeight: "33px",
  };

  const onComponentsChange = (
    components: IApplicationComponentPropertyValueModel[]
  ) => {
    let bannerItemsCopy = [...bannerItems];

    for (const component of components) {
      const componentIndex = bannerItems.findIndex(
        (row) => row?.Guid === component.Guid
      );

      if (componentIndex >= 0) {
        bannerItemsCopy[componentIndex] = component;
      }
    }

    if (bannerItemsCopy) {
      if (onNewChanges) {
        setBannerItems(bannerItemsCopy);
        setHasChanges(true);
        onNewChanges(true);
      }
    }
  };

  const onComponentChange = (component: IApplicationComponentModel) => {
    const componentIndex = bannerItems.findIndex((row) => {
      if (row.Component) return row?.Component.Guid === component.Guid;
    });

    if (componentIndex >= 0) {
      let componentToUpdate = bannerItems[componentIndex];

      if (onNewChanges) {
        setSelectedBannerItem(componentToUpdate);
        setHasChanges(true);
        onNewChanges(true);
      }
    }
  };

  const sider = useCallback(
    (selectedBannerItem) => {
      if (!selectedBannerItem?.Component) {
        return <></>;
      }

      let componentTypeTag: React.ReactNode;

      if (component && component.RecordStatus !== RecordStatus.Deleted) {
        componentTypeTag = ComponentTypeHelper.getTag(
          selectedBannerItem?.Component?.ComponentTypeCode,
          {
            marginLeft: "16px",
          }
        );
      }

      return (
        <Sider
          className="ApplicationBannerItems__Properties"
          collapsed={!selectedBannerItem}
          reverseArrow={true}
          collapsedWidth={0}
          width={440}
          zeroWidthTriggerStyle={zeroWidthTriggerStyle}
        >
          <SectionGrid>
            <SectionGridItem>
              <Heading
                title={
                  <>
                    {t("Properties")}
                    {componentTypeTag}
                  </>
                }
              />
              <DesignerModule.Components.ApplicationBannerItemProperties
                key={`BannerItemProperties-${selectedBannerItem.Component.Guid}`}
                component={selectedBannerItem.Component}
                onComponentChange={onComponentChange}
              />
            </SectionGridItem>
          </SectionGrid>
        </Sider>
      );
    },
    [selectedBannerItem, hasChanges]
  );

  return (
    <Modal
      title={t("ITEMS")}
      onCancel={() => {
        onItemsModalCancel();
        saveChangesOnClose();
      }}
      width="700px"
      style={{
        marginRight: "40vw",
        width: "700px",
      }}
      visible={modalVisible}
      footer={
        <>
          <Button
            key="cancel"
            onClick={() => {
              onItemsModalCancel();
              saveChangesOnClose();
            }}
          >
            Close
          </Button>
        </>
      }
    >
      <div className="BannerItemAddForm">
        <div className="BannerItemAddForm__Content">
          <div className="BannerItemAddForm__Table-header">
            <div className="title">
              <h1>{t("Banner items")}</h1>
            </div>
            <div className="actions">
              <Button
                icon={<Icon type="plus" />}
                onClick={addBannerItem}
                title={t("Add item")}
              />
            </div>
          </div>
          <TableWithDraggableSorter<IApplicationComponentModel>
            dragType="handler"
            columns={columns}
            dataSource={componentsToView}
            loading={isLoadingData}
            rowSelection={rowSelection}
            onRow={onTableRow}
            onMoveRow={onMoveRow}
          />
        </div>
      </div>
      {sider(selectedBannerItem)}
    </Modal>
  );
};
